import React from 'react';
import { Users, Clock, Target, CalendarDays, DollarSign, PiggyBank, BarChart2, Globe2, TrendingDown } from 'lucide-react';

export interface CalculatorMetrics {
  totalVolunteers: number;
  totalVolunteerHours: number;
  engagedWorkforcePercent: number;
  totalEventsRequired: number;
  totalProgramCost: number;
  avgCostPerVolunteer: number;
  costImpactRatio: string;
  beneficiariesReached: number;
  attritionReductionPercent: number;
  sdgTags: string;
  percentEventsWithUnDays: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  Participation: 'bg-blue-50 border-blue-200',
  Budget: 'bg-yellow-50 border-yellow-200',
  Impact: 'bg-green-50 border-green-200',
};

interface Props {
  metrics: CalculatorMetrics;
  currency: 'INR' | 'USD';
}

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
};

const BudgetCalculatorCalculatedMetricsSection: React.FC<Props> = ({ metrics, currency }) => {

  return (
    <section className="mb-8 p-4 bg-gray-50 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <span role="img" aria-label="chart">📊</span> Calculated Metrics
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Participation */}
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Participation']}`}>
          <Users className="w-7 h-7 text-blue-600" />
          <span className="font-semibold text-gray-700">Total Volunteers</span>
          <span className="text-2xl font-bold text-gray-900">{currency === 'INR' ? metrics.totalVolunteers.toLocaleString('en-IN') : metrics.totalVolunteers.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Participation</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Participation']}`}>
          <Clock className="w-7 h-7 text-blue-600" />
          <span className="font-semibold text-gray-700">Total Volunteer Hours</span>
          <span className="text-2xl font-bold text-gray-900">{currency === 'INR' ? metrics.totalVolunteerHours.toLocaleString('en-IN') : metrics.totalVolunteerHours.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Participation</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Participation']}`}>
          <Target className="w-7 h-7 text-blue-600" />
          <span className="font-semibold text-gray-700">% Engaged Workforce</span>
          <span className="text-2xl font-bold text-gray-900">{metrics.engagedWorkforcePercent}%</span>
          <span className="text-xs text-gray-400 mt-1">Participation</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Participation']}`}>
          <CalendarDays className="w-7 h-7 text-blue-600" />
          <span className="font-semibold text-gray-700">Total Events Required</span>
          <span className="text-2xl font-bold text-gray-900">{currency === 'INR' ? metrics.totalEventsRequired.toLocaleString('en-IN') : metrics.totalEventsRequired.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Participation</span>
        </div>
        {/* Budget */}
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Budget']}`}>
          <DollarSign className="w-7 h-7 text-yellow-600" />
          <span className="font-semibold text-gray-700">Total Program Cost</span>
          <span className="text-2xl font-bold text-gray-900">{CURRENCY_SYMBOLS[currency]} {currency === 'INR' ? metrics.totalProgramCost.toLocaleString('en-IN') : metrics.totalProgramCost.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Budget</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Budget']}`}>
          <PiggyBank className="w-7 h-7 text-yellow-600" />
          <span className="font-semibold text-gray-700">Avg. Cost per Volunteer</span>
          <span className="text-2xl font-bold text-gray-900">{CURRENCY_SYMBOLS[currency]} {currency === 'INR' ? metrics.avgCostPerVolunteer.toLocaleString('en-IN') : metrics.avgCostPerVolunteer.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Budget</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Budget']}`}>
          <BarChart2 className="w-7 h-7 text-yellow-600" />
          <span className="font-semibold text-gray-700">Cost vs Impact Ratio</span>
          <span className="text-2xl font-bold text-gray-900">{metrics.costImpactRatio}</span>
          <span className="text-xs text-gray-400 mt-1">Budget</span>
        </div>
        {/* Impact */}
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Impact']}`}>
          <Globe2 className="w-7 h-7 text-green-700" />
          <span className="font-semibold text-gray-700">Beneficiaries Reached</span>
          <span className="text-2xl font-bold text-gray-900">{currency === 'INR' ? metrics.beneficiariesReached.toLocaleString('en-IN') : metrics.beneficiariesReached.toLocaleString('en-US')}</span>
          <span className="text-xs text-gray-400 mt-1">Impact</span>
        </div>
        <div className={`rounded-lg border p-4 flex flex-col items-start gap-2 shadow-sm ${CATEGORY_COLORS['Impact']}`}>
          <TrendingDown className="w-7 h-7 text-green-700" />
          <span className="font-semibold text-gray-700">Est. Attrition Reduction %</span>
          <span className="text-2xl font-bold text-gray-900">{metrics.attritionReductionPercent}%</span>
          <span className="text-xs text-gray-400 mt-1">Impact</span>
        </div>

      </div>
    </section>
  );
};

export default BudgetCalculatorCalculatedMetricsSection;
